import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import NullStateAnalytic from '@alltrails/amplitude/enums/NullState';
import NullStateAction from '@alltrails/amplitude/enums/NullStateAction';
import NullState from '@alltrails/denali/components/NullState';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Tent from '@alltrails/denali/illustrations/Tent';
import { useAppSelector } from '../../../redux';
import { type SearchType, useLazySearchReviewsQuery } from '../../../slices/reviewsApi';
import styles from './SearchError.module.scss';

type SearchErrorProps = {
  onCtaClick?: (action: NullStateAction, state: NullStateAnalytic) => void;
  onViewed?: (state: NullStateAnalytic) => void;
  searchType: SearchType;
};

const SearchError = ({ onCtaClick, onViewed, searchType }: SearchErrorProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const { query, entityType, ratings, activities, months } = useAppSelector(state => state.reviewsSearchFilters.permanent);
  const [trigger] = useLazySearchReviewsQuery();

  const retry = () => {
    onCtaClick?.('retry', window.navigator.onLine ? 'server error' : 'no service');
    trigger({
      query,
      photosOnly: entityType === 'photosOnly',
      rating: ratings,
      activity: activities,
      month: months,
      ...searchType
    });
  };

  useEffect(() => {
    onViewed?.(window.navigator.onLine ? 'server error' : 'no service');
  }, [onViewed]);

  return (
    <div className={styles.container}>
      {window.navigator.onLine ? (
        <NullState
          buttons={{
            [isMobile ? 'primary' : 'default']: {
              onClick: retry,
              text: intl.formatMessage({ defaultMessage: 'Try again' })
            }
          }}
          className={styles.error}
          description={intl.formatMessage({
            defaultMessage: 'It’s us, not you! Try clearing your search or check back later.'
          })}
          illustration={{ Component: Tent }}
          noTopMargin
          title={intl.formatMessage({ defaultMessage: 'Something’s not working' })}
        />
      ) : (
        <NullState
          buttons={{
            default: {
              onClick: retry,
              text: intl.formatMessage({ defaultMessage: 'Try again' })
            }
          }}
          className={styles.error}
          description={intl.formatMessage({ defaultMessage: 'Check your internet connection.' })}
          illustration={{ Component: Tent }}
          noTopMargin
          title={intl.formatMessage({ defaultMessage: 'You’re offline' })}
        />
      )}
    </div>
  );
};

export default SearchError;
